import React from "react";
import { toast } from "react-toastify";

import { DOCUMENT_FILE_MAX_BYTES, DOCUMENT_FILE_TYPES } from "../api/documentsApi";
import {
  getPaymentFileBlobApi,
  PaymentArea,
  PaymentAttachments,
  PaymentAttachmentsPayload,
  PaymentFileKind,
  uploadPaymentFileApi,
} from "../api/paymentFilesApi";
import DocumentFileButton from "./DocumentFileButton";

/** Arquivos escolhidos no modal do recolhimento, ainda não enviados. */
export type AttachmentDraft = { guide: File | null; receipt: File | null; removeGuide: boolean; removeReceipt: boolean };
export const EMPTY_ATTACHMENT_DRAFT: AttachmentDraft = { guide: null, receipt: null, removeGuide: false, removeReceipt: false };

const KINDS: Array<{ kind: PaymentFileKind; label: string }> = [
  { kind: "guide", label: "Guia" },
  { kind: "receipt", label: "Comprovante" },
];

/** Envia o que foi escolhido e monta o trecho do corpo do lançamento (só o que mudou). */
export async function resolveAttachments(token: string, area: PaymentArea, draft: AttachmentDraft): Promise<PaymentAttachmentsPayload> {
  const payload: PaymentAttachmentsPayload = {};
  if (draft.guide) {
    const up = await uploadPaymentFileApi(token, area, draft.guide);
    payload.guideAssetId = up.assetId;
    payload.guideFileName = up.fileName ?? draft.guide.name;
  } else if (draft.removeGuide) payload.guideAssetId = null;
  if (draft.receipt) {
    const up = await uploadPaymentFileApi(token, area, draft.receipt);
    payload.receiptAssetId = up.assetId;
    payload.receiptFileName = up.fileName ?? draft.receipt.name;
  } else if (draft.removeReceipt) payload.receiptAssetId = null;
  return payload;
}

type FieldsProps = {
  idPrefix: string;
  current?: PaymentAttachments;
  draft: AttachmentDraft;
  onChange: (draft: AttachmentDraft) => void;
  disabled?: boolean;
};

/** Campos "Guia" e "Comprovante" do modal (PDF ou imagem, até 10 MB). */
export function PaymentAttachmentFields({ idPrefix, current, draft, onChange, disabled }: FieldsProps) {
  const pick = (kind: PaymentFileKind, file: File | undefined) => {
    if (!file) return;
    if (!DOCUMENT_FILE_TYPES.includes(file.type)) return toast.error("Envie PDF, JPG, PNG ou WEBP.");
    if (file.size > DOCUMENT_FILE_MAX_BYTES) return toast.error("O arquivo pode ter no máximo 10 MB.");
    onChange(kind === "guide" ? { ...draft, guide: file, removeGuide: false } : { ...draft, receipt: file, removeReceipt: false });
  };

  return (
    <div className="dpFormGrid dpFormGrid--two">
      {KINDS.map(({ kind, label }) => {
        const has = kind === "guide" ? current?.hasGuide : current?.hasReceipt;
        const fileName = kind === "guide" ? current?.guideFileName : current?.receiptFileName;
        const chosen = kind === "guide" ? draft.guide : draft.receipt;
        const removing = kind === "guide" ? draft.removeGuide : draft.removeReceipt;
        return (
          <div className="dpFormGroup" key={kind}>
            <label htmlFor={`${idPrefix}-${kind}`}>{label} (PDF ou imagem)</label>
            <input
              id={`${idPrefix}-${kind}`}
              type="file"
              className="dpInput"
              accept={DOCUMENT_FILE_TYPES.join(",")}
              disabled={disabled}
              onChange={(e) => pick(kind, e.target.files?.[0])}
            />
            <span className="dpFormHint">
              {chosen ? chosen.name : removing ? "Será removido ao salvar." : has ? `Anexado: ${fileName || "arquivo"}. Enviar outro substitui.` : "Opcional."}
            </span>
            {has && !chosen ? (
              <label className="dpFormHint" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={removing}
                  disabled={disabled}
                  onChange={(e) => onChange(kind === "guide" ? { ...draft, removeGuide: e.target.checked } : { ...draft, removeReceipt: e.target.checked })}
                />
                Remover o anexo
              </label>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** Botões da tabela para abrir a guia e o comprovante (só os que existem). */
export function PaymentFileButtons({ token, area, id, files }: { token: string; area: PaymentArea; id: string; files: PaymentAttachments }) {
  return (
    <>
      {KINDS.filter(({ kind }) => (kind === "guide" ? files.hasGuide : files.hasReceipt)).map(({ kind, label }) => (
        <DocumentFileButton
          key={kind}
          token={token}
          label={label}
          className="dpBtn dpBtn--secondary dpBtn--small"
          fileName={kind === "guide" ? files.guideFileName : files.receiptFileName}
          fetchBlob={() => getPaymentFileBlobApi(token, area, id, kind)}
        />
      ))}
    </>
  );
}
